// CRM layer — unified notification bell (added Phase 6-D).
//
// One place that aggregates everything currently waiting on the signed-in user:
//   · pending follow-up transfers addressed to them or their departments
//   · their own opportunities the reminder engine has chased
//   · for admins, opportunities the engine has dropped into the unassigned pool
//
// This ADDS to the existing distributed badges (sidebar transfer count, card
// reminder chips); it does not replace them. Same 30s poll as the sidebar.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import {
  crmIncomingTransfers, crmMyOpportunities, crmGetUnassigned,
  crmNav, crmGo, STAGE_LABEL, fmtHours
} from './crmApi';

const POLL_MS = 30000;

const CrmNotificationBell = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [attention, setAttention] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const wrapRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    // Each source is independent: one failing must not blank the whole bell.
    crmIncomingTransfers().then((r) => setTransfers(r.data.transfers)).catch(() => {});
    crmMyOpportunities()
      .then((r) => setAttention(r.data.opportunities.filter((o) => o.needsAttention)))
      .catch(() => {});
    if (isAdmin) {
      crmGetUnassigned().then((r) => setUnassigned(r.data.opportunities)).catch(() => {});
    } else {
      setUnassigned([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [user, load]);

  // Close when clicking anywhere else.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!user) return null;

  const total = transfers.length + attention.length + unassigned.length;

  const goOpportunity = (id) => { crmNav.setOpportunity(id); crmGo('crm-opportunity'); setOpen(false); };
  const goTransfers = () => { crmGo('crm-transfers'); setOpen(false); };
  const goPool = () => { crmGo('crm-admin'); setOpen(false); };

  return (
    <div className="crm-bell-wrap crm-scope" ref={wrapRef}>
      <button className="crm-bell" onClick={() => setOpen(!open)}
        title={total ? `${total} تنبيه بحاجة لانتباهك` : 'لا توجد تنبيهات'}>
        <FiBell size={19} />
        {total > 0 && <span className="crm-bell-count">{total > 99 ? '99+' : total}</span>}
      </button>

      {open && (
        <div className="crm-bell-panel">
          {total === 0 && <div className="crm-bell-empty">لا توجد تنبيهات — كل شيء تحت السيطرة</div>}

          {transfers.length > 0 && (
            <>
              <div className="crm-bell-group">متابعات محوّلة إليك ({transfers.length})</div>
              {transfers.slice(0, 5).map((t) => (
                <button key={t.id} className="crm-bell-item" onClick={goTransfers}>
                  <div className="t">{t.from_name || t.from_extension} حوّل إليك متابعة</div>
                  <div className="s">
                    {t.to_extension ? 'موجّه إليك مباشرة' : `لقسم ${t.to_department}`} ·{' '}
                    <span style={{ direction: 'ltr', display: 'inline-block' }}>{t.phone_number}</span>
                  </div>
                </button>
              ))}
              {transfers.length > 5 && (
                <button className="crm-bell-item" onClick={goTransfers}>
                  <div className="s">عرض كل المتابعات ({transfers.length})</div>
                </button>
              )}
            </>
          )}

          {attention.length > 0 && (
            <>
              <div className="crm-bell-group">فرصك التي تحتاج متابعة ({attention.length})</div>
              {attention.slice(0, 5).map((o) => (
                <button key={o.id} className="crm-bell-item" onClick={() => goOpportunity(o.id)}>
                  <div className="t">{o.opportunity_type} · {STAGE_LABEL[o.crm_stage]}</div>
                  <div className="s">
                    {o.reminder_count} تذكير · في هذه المرحلة منذ {fmtHours(o.hoursInStage)}
                  </div>
                </button>
              ))}
            </>
          )}

          {isAdmin && unassigned.length > 0 && (
            <>
              <div className="crm-bell-group">فرص سُحب إسنادها ({unassigned.length})</div>
              {unassigned.slice(0, 5).map((o) => (
                <button key={o.id} className="crm-bell-item" onClick={goPool}>
                  <div className="t">{o.opportunity_type} — {o.client_name || o.phone_number}</div>
                  <div className="s">
                    {o.previous_agent ? `كانت لدى ${o.previous_agent}` : 'بلا موظف'}
                    {o.hours_unassigned != null ? ` · منذ ${fmtHours(o.hours_unassigned)}` : ''}
                  </div>
                </button>
              ))}
              {unassigned.length > 5 && (
                <button className="crm-bell-item" onClick={goPool}>
                  <div className="s">عرض قائمة غير المسندة ({unassigned.length})</div>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CrmNotificationBell;
